import type { ProcessedRow } from './types';
import type { ICellRendererParams } from '@ag-grid-community/core';
import { faMessageDots } from '@fortawesome/pro-regular-svg-icons/faMessageDots';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEllipsisActive } from '@thinkalpha/ui-core/util/dom';
import { cn } from '@thinkalpha/ui-core/utils';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Tooltip } from 'src/components/ui';

const truncatedClassName = 'truncated-message';

interface IMessageCellRendererParams extends ICellRendererParams {
    data: ProcessedRow;
}

export const DetailedMessageCellRenderer = (props: IMessageCellRendererParams) => {
    const [messageCell, setMessageCell] = useState<HTMLDivElement>();

    const message = props.valueFormatted;

    const [showTooltip, setShowTooltip] = useState(false);

    useLayoutEffect(() => {
        const truncatedMessageCell = props.eGridCell.getElementsByClassName(
            truncatedClassName,
        )[0] as unknown as HTMLDivElement;

        setMessageCell(truncatedMessageCell);
    }, [props.eGridCell]);

    useEffect(() => {
        if (!messageCell) {
            return;
        }

        setShowTooltip(isEllipsisActive(messageCell));

        // Resize observer watches for the size based on the parent
        const resizeObserver = new ResizeObserver(() => {
            setShowTooltip(isEllipsisActive(messageCell));
        });

        // Listen to the parent, since flexbox children technically keep the same width (100%)
        resizeObserver.observe(messageCell.parentElement!, {
            box: 'border-box',
        });

        return () => {
            resizeObserver.disconnect();
        };
    }, [messageCell, setShowTooltip]);

    return (
        <div className="flex w-full items-center">
            <div className={cn(truncatedClassName, 'overflow-hidden text-ellipsis')}>{message}</div>
            {showTooltip ? (
                <Tooltip content={message ?? ''} side="left">
                    <FontAwesomeIcon icon={faMessageDots} />
                </Tooltip>
            ) : null}
        </div>
    );
};
